import axios from "axios";

const API = axios.create({
  baseURL: "https://coaching-management-system-diun.onrender.com/"
});

/**
 * Request Interceptor: Attach JWT token to all requests
 * Reads token from localStorage and adds it to Authorization header
 */
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor: Handle token expiration
 * If token is invalid or expired (401), clear localStorage and redirect to login
 */
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("authToken");
      localStorage.removeItem("admin");
      window.location.href = "/"; // Redirect to home/login page
    }
    return Promise.reject(error);
  }
);

export const loginAdmin = (data) => API.post("/api/auth/login", data);

export const getStudents = (classFilter = 'all', options = {}) => {
  const params = classFilter !== 'all' ? { class: classFilter } : {};
  Object.assign(params, options);
  return API.get("/api/students", { params });
};
export const getStudentsByDob = ({ month, day, classFilter = "all", includeImages = false }) => {
  const params = {
    dobMonth: month,
    dobDay: day,
    includeImages
  };
  if (classFilter !== "all") {
    params.class = classFilter;
  }
  return API.get("/api/students", { params });
};
export const updateStudentWish = (id, data) => API.put(`/api/students/${id}`, data);
export const getStudent = (id, options = {}) => API.get(`/api/students/${id}`, { params: options });
export const getClasses = () => API.get("/api/classes");
export const addStudent = (data) => API.post("/api/add-student", data);
export const bulkAddStudents = (data) => API.post("/api/students/bulk", data);
export const updateStudent = (id, data) => API.put(`/api/students/${id}`, data);
export const deleteStudent = (id) => API.delete(`/api/students/${id}`);
export const markAttendance = (data) => API.post("/api/attendance/mark", data);
export const sendWhatsappTest = (data = {}) => API.post("/api/whatsapp/test", data);
export const getAttendance = (date, classFilter = "all", filters = {}) => {
  const params = {};
  if (date) {
    params.date = date;
  }
  if (classFilter !== "all") {
    params.class = classFilter;
  }
  Object.assign(params, filters);
  return API.get("/api/attendance", { params });
};
export const deleteAttendanceRecord = (id) => API.delete(`/api/attendance/${id}`);
export const deleteAttendanceRecords = (ids) => API.delete("/api/attendance", { data: { ids } });
export const getFees = (filters = {}) => API.get("/api/fees", { params: filters });
export const addFee = (data) => API.post("/api/fees", data);
export const updateFee = (id, data) => API.put(`/api/fees/${id}`, data);
export const deleteFee = (id) => API.delete(`/api/fees/${id}`);
export const getSalaries = (filters = {}) => API.get("/api/salaries", { params: filters });
export const addSalary = (data) => API.post("/api/salaries", data);
export const updateSalary = (id, data) => API.put(`/api/salaries/${id}`, data);
export const deleteSalary = (id) => API.delete(`/api/salaries/${id}`);
export const getFestivals = (fromDate) => {
  const params = fromDate ? { fromDate } : {};
  return API.get("/api/festivals", { params });
};
export const createInquiry = (data) => API.post("/api/inquiries", data);
export const getInquiries = () => API.get("/api/inquiries");
export const updateInquiry = (id, data) => API.patch(`/api/inquiries/${id}`, data);
export const deleteInquiry = (id) => API.delete(`/api/inquiries/${id}`);
export const getShiningStars = (includeInactive = false) =>
  API.get("/api/shining-stars", { params: includeInactive ? { includeInactive: true } : {} });
export const addShiningStar = (data) => API.post("/api/shining-stars", data);
export const updateShiningStar = (id, data) => API.put(`/api/shining-stars/${id}`, data);
export const deleteShiningStar = (id) => API.delete(`/api/shining-stars/${id}`);
export const getCampusActivities = (includeInactive = false) =>
  API.get("/api/campus-activities", { params: includeInactive ? { includeInactive: true } : {} });
export const addCampusActivity = (data) => API.post("/api/campus-activities", data);
export const updateCampusActivity = (id, data) => API.put(`/api/campus-activities/${id}`, data);
export const deleteCampusActivity = (id) => API.delete(`/api/campus-activities/${id}`);
export const getAboutCarouselSlides = (includeInactive = false) =>
  API.get("/api/about-carousel", { params: includeInactive ? { includeInactive: true } : {} });
export const addAboutCarouselSlide = (data) => API.post("/api/about-carousel", data);
export const updateAboutCarouselSlide = (id, data) => API.put(`/api/about-carousel/${id}`, data);
export const deleteAboutCarouselSlide = (id) => API.delete(`/api/about-carousel/${id}`);
export const getTestMarks = (filters = {}) => API.get("/api/test-marks", { params: filters });
export const addTestMark = (data) => API.post("/api/test-marks", data);
export const updateTestMark = (id, data) => API.put(`/api/test-marks/${id}`, data);
export const deleteTestMark = (id) => API.delete(`/api/test-marks/${id}`);
export const getTestMarkSubjects = () => API.get("/api/test-marks/subjects");
export const getAttendanceReport = (filters) => API.get("/api/reports/attendance", { params: filters });
export const getTestMarkReport = (filters) => API.get("/api/reports/test-marks", { params: filters });
export const downloadTestMarkReport = (filters) =>
  API.get("/api/reports/test-marks/export", { params: filters, responseType: "blob" });
export const downloadStudentsReport = (filters = {}) =>
  API.get("/api/reports/students/export", { params: filters, responseType: "blob" });
export const downloadTeachersReport = (filters = {}) =>
  API.get("/api/reports/teachers/export", { params: filters, responseType: "blob" });
export const downloadAttendanceReport = (filters) =>
  API.get("/api/reports/attendance/export", { params: filters, responseType: "blob" });

// Teacher API functions
export const getTeachers = (subjectFilter = 'all', options = {}) => {
  const params = subjectFilter !== 'all' ? { subject: subjectFilter } : {};
  Object.assign(params, options);
  return API.get("/api/teachers", { params });
};
export const getTeacher = (id, options = {}) => API.get(`/api/teachers/${id}`, { params: options });
export const getSubjects = () => API.get("/api/subjects");
export const addTeacher = (data) => API.post("/api/add-teacher", data);
export const bulkAddTeachers = (data) => API.post("/api/teachers/bulk", data);
export const updateTeacher = (id, data) => API.put(`/api/teachers/${id}`, data);
export const deleteTeacher = (id) => API.delete(`/api/teachers/${id}`);
